import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { Mail, Phone, MapPin, ArrowLeft } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { SmoothScroll } from '../components/SmoothScroll';
import { Header } from '../components/Header';
import { Contact } from '../components/Contact';
import { Footer } from '../components/Footer';
import { mockData } from '../mock';

export const ContactPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      let attempts = 0;
      const interval = setInterval(() => {
        const el = document.querySelector(location.hash);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
          clearInterval(interval);
        } else if (++attempts > 20) {
          clearInterval(interval);
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [location.hash]);
  const infoBlocks = [
    {
      Icon: Mail,
      label: 'Email',
      value: mockData.contact.email,
      href: `mailto:${mockData.contact.email}`
    },
    {
      Icon: Phone,
      label: 'Phone',
      value: mockData.contact.phone,
      href: `tel:${mockData.contact.phone}`
    },
    {
      Icon: MapPin,
      label: 'Location',
      value: mockData.contact.location,
      href: null
    }
  ];

  return (
    <SmoothScroll>
      <div className="relative bg-slate-50">
        <Header />

        {/* Hero band */}
        <section className="relative pt-40 pb-20 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 overflow-hidden">
          {/* Grid background */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-20" />

          {/* Spotlight */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.12 }}
            transition={{ duration: 1.5 }}
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-amber-500 rounded-full blur-[150px]"
          />

          <div className="relative z-10 max-w-7xl mx-auto px-6">
            {/* Back link */}
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-10"
            >
              <Link
                to="/"
                data-testid="back-to-home-link"
                className="inline-flex items-center gap-2 text-slate-300 hover:text-amber-400 transition-colors duration-300 group"
              >
                <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1" />
                <span className="text-sm tracking-wide uppercase">Back to Home</span>
              </Link>
            </motion.div>

            {/* Title */}
            <div className="max-w-4xl">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-amber-400 text-sm tracking-[0.3em] uppercase mb-6"
              >
                Get in touch
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.4, 0.25, 1] }}
                className="text-5xl md:text-7xl font-bold leading-tight mb-8"
              >
                <span className="bg-gradient-to-r from-white via-amber-50 to-amber-400 bg-clip-text text-transparent">
                  Let's build resilience together.
                </span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-slate-300 max-w-2xl font-light leading-relaxed"
              >
                Whether you need a full-scale OT security assessment or a quick conversation about your automation project — we're here. Contact us today for a no-obligation quote.
              </motion.p>
            </div>

            {/* Info blocks */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {infoBlocks.map(({ Icon, label, value, href }, i) => {
                const Wrapper = href ? 'a' : 'div';
                return (
                  <motion.div
                    key={label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.6 + i * 0.08 }}
                    whileHover={{ y: -4 }}
                  >
                    <Wrapper
                      href={href || undefined}
                      data-testid={`contact-info-${label.toLowerCase()}`}
                      className="block h-full p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl hover:border-amber-400/50 transition-all duration-300 group"
                    >
                      <div className="w-11 h-11 bg-gradient-to-br from-amber-400 to-amber-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                        <Icon className="w-5 h-5 text-slate-950" />
                      </div>
                      <div className="text-slate-400 text-xs tracking-wider uppercase mb-1">
                        {label}
                      </div>
                      <div className="text-white font-semibold text-base break-words">
                        {value}
                      </div>
                    </Wrapper>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        {/* Reuse existing Contact component for the full form + info panel */}
        <Contact />

        <Footer />
      </div>
    </SmoothScroll>
  );
};

export default ContactPage;
